import { GridColumn as Column } from "@progress/kendo-react-grid/dist/npm/GridColumn";
import * as React from "react";
import { useContext } from "react";
import { GridCell } from "@progress/kendo-react-grid/dist/npm/cells/GridCell";
import { EditProjectContext } from "../../../EditProjectContext";
import { ProjectState } from "../../../../ViewModel/ProjectState";
import { generateOdcColumns } from "../GenerateOdcColumns";
import { EngineeredSFDropdownCell } from "./EngineeredSFDropdownCell";
import { ColumnMenu } from "../../../../../Layout/Grid/Filter/ColumnMenu";
import { roundToTwoDecimals } from "../../../../../Grid/GridConstants";
import { SpdrClientViewModel } from "../../../../ViewModel/SpdrClientViewModel";
import { isArrayUndefinedOrEmpty } from "../../../../../Utils/arrays";
import { NumericalKey } from "../../../../../Utils/utilityTypes";
import { KendoReactDataGridFooterSumCell } from "../../../../../Layout/Grid/Cell/KendoReactDataGridFooterSumCell";
import { KendoReactColoredFooterCell } from "../../../../../Layout/Grid/Cell/KendoReactColoredFooterCell";
import { ReadOnlyProps } from "../../../ReadOnlyProps";

type Props = {
  odcGsdbsForColumns: Array<string> | undefined;
  ftlRouteNumber: string | undefined;
  spdrsOnThisRoute: Array<SpdrClientViewModel>;
};

export default function InboundPipelineGridColumns({
  odcGsdbsForColumns,
  ftlRouteNumber,
  spdrsOnThisRoute,
  isReadOnly,
}: Props & ReadOnlyProps) {
  const { project } = useContext(EditProjectContext);
  const editable = project.State === ProjectState.Sandbox && !isReadOnly;

  const kendoReactFooterSumCell = (fieldKey: NumericalKey<SpdrClientViewModel>) => {
    return !isArrayUndefinedOrEmpty(spdrsOnThisRoute) ? (
      <KendoReactDataGridFooterSumCell items={spdrsOnThisRoute || []} fieldKey={fieldKey} />
    ) : (
      <td />
    );
  };

  const kendoReactColoredFooterCell = (cellText?: string) => {
    return !isArrayUndefinedOrEmpty(spdrsOnThisRoute) ? (
      <KendoReactColoredFooterCell cellText={cellText} />
    ) : (
      <td />
    );
  };

  const defaultColumns = [
    <Column
      key="SupplierGsdb"
      field="SupplierGsdb"
      title="Supplier"
      filter="text"
      columnMenu={ColumnMenu}
      editable={false}
      resizable
      footerCell={() => kendoReactColoredFooterCell("Total")}
    />,
    <Column
      key="PlantGsdb"
      field="PlantGsdb"
      title="Plant"
      filter="text"
      columnMenu={ColumnMenu}
      editable={false}
      resizable
      footerCell={() => kendoReactColoredFooterCell()}
    />,
    <Column
      key="DockCode"
      field="DockCode"
      title="Dock"
      filter="text"
      columnMenu={ColumnMenu}
      editable={false}
      resizable
      footerCell={() => kendoReactColoredFooterCell()}
    />,
    <Column
      key="InboundMegaLdm"
      field="InboundMegaLdm"
      title="MLDM"
      filter="numeric"
      columnMenu={ColumnMenu}
      editable={false}
      resizable
      format={roundToTwoDecimals}
      footerCell={(props) => kendoReactFooterSumCell("InboundMegaLdm")}
    />,
    <Column
      key="InboundEuroLdm"
      field="InboundEuroLdm"
      title="ELDM"
      filter="numeric"
      columnMenu={ColumnMenu}
      editable={false}
      resizable
      format={roundToTwoDecimals}
      footerCell={(props) => kendoReactFooterSumCell("InboundEuroLdm")}
    />,
    <Column
      key="InboundKg"
      field="InboundKg"
      title="KG"
      filter="numeric"
      columnMenu={ColumnMenu}
      editable={false}
      resizable
      format={roundToTwoDecimals}
      footerCell={(props) => kendoReactFooterSumCell("InboundKg")}
    />,
    <Column
      key="InboundCublicMeters"
      field="InboundCublicMeters"
      title="M3"
      filter="numeric"
      columnMenu={ColumnMenu}
      editable={false}
      resizable
      format={roundToTwoDecimals}
      footerCell={() => kendoReactFooterSumCell("InboundCublicMeters")}
    />,
    <Column
      key="ShipFrequency"
      field="ShipFrequency"
      title="Engineered SF"
      filter="numeric"
      columnMenu={ColumnMenu}
      editable={editable}
      cell={editable ? EngineeredSFDropdownCell : GridCell}
      resizable
      footerCell={() => kendoReactColoredFooterCell()}
    />,
  ];

  return defaultColumns.concat(
    generateOdcColumns({
      odcs: odcGsdbsForColumns,
      ftlRouteNumber,
      clientViewModels: spdrsOnThisRoute,
      disableColumn: isReadOnly,
    })
  );
}
